import {
  ErrorResponse,
  OtpResponse,
  RegistrationResponse,
  ServiceResponse,
  ValidateOtpResponse,
} from '@bzbs/react-api-client';
import { create } from 'zustand';
import { isValidEmail, trackCommonEvents } from '../utils';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';
import { useLocaleStore } from './locale.store';

export type RegistrationFormData = {
  username: string;
  password: string;
  confirmPassword: string;
  contactNumber: string;
  firstName: string;
  lastName: string;
  otp: string;
  refCode: string;
  address?: string;
  gender?: string;
  birthDate?: number;
  email?: string;
  refUserCode?: string;
  zipCode?: string;
  info?: string;
  termAndConditionAccepted: boolean;
  dataPrivacyAccepted: boolean;
  marketingAccepted: boolean;
  consentAge?: number;
  consentAgeAccepted: boolean;
  emailMarketingAccepted: boolean;
  smsMarketingAccepted: boolean;
  notificationMarketingAccepted: boolean;
  lineMarketingAccepted: boolean;
  phoneMarketingAccepted: boolean;
  additionalInfo?: { [key: string]: unknown };
};

export type RegistrationState = {
  form: RegistrationFormData;
  consentVersion: {
    termAndCondition: string;
    dataPrivacy: string;
    marketingOptions: string;
  } | null;
  otpExpireInSeconds: number;
  isLoading: boolean;
  error: ErrorResponse | null;
};

export type RegistrationActions = {
  updateForm: (updates: Partial<RegistrationFormData>) => void;
  setError: (value: ErrorResponse | null) => void;
  clearData: () => void;
  sendOtp: () => Promise<ServiceResponse<OtpResponse>>;
  validateOtp: () => Promise<ServiceResponse<ValidateOtpResponse>>;
  validateRegister: () => Promise<ServiceResponse<OtpResponse>>;
  register: () => Promise<ServiceResponse<RegistrationResponse>>;
  setConsentVersion: (termAndCondition: string, dataPrivacy: string, marketingOptions: string) => void;
  clear: () => void;
};

export type RegistrationStore = RegistrationState & RegistrationActions;

const initialFormData: RegistrationFormData = {
  username: '',
  password: '',
  confirmPassword: '',
  contactNumber: '',
  firstName: '',
  lastName: '',
  otp: '',
  refCode: '',
  address: undefined,
  gender: undefined,
  birthDate: undefined,
  email: undefined,
  refUserCode: undefined,
  zipCode: undefined,
  info: undefined,
  termAndConditionAccepted: false,
  dataPrivacyAccepted: false,
  marketingAccepted: false,
  consentAge: undefined,
  consentAgeAccepted: false,
  emailMarketingAccepted: false,
  smsMarketingAccepted: false,
  notificationMarketingAccepted: false,
  lineMarketingAccepted: false,
  phoneMarketingAccepted: false,
  additionalInfo: undefined,
};

const initialConsentVersion = {
  termAndCondition: '1',
  dataPrivacy: '1',
  marketingOptions: '1',
};

export const useRegistrationStore = create<RegistrationStore>((set, get) => {
  return {
    form: { ...initialFormData },
    consentVersion: initialConsentVersion,
    otpExpireInSeconds: 180,
    isLoading: false,
    error: null,

    updateForm: (updates: Partial<RegistrationFormData>) => set((state) => ({ form: { ...state.form, ...updates } })),

    setError: (value: ErrorResponse | null) => set({ error: value }),

    clearData: () => {
      set({
        form: { ...initialFormData },
        consentVersion: initialConsentVersion,
      });
    },

    sendOtp: async () => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      const uuid = useBuzzebeesAppStore.getState().uuid;
      const appId = useBuzzebeesAppStore.getState().appId;
      const contactNumber = get().form.contactNumber.trim();

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      if (contactNumber === '') {
        return { type: 'client-error', message: 'Contact number is required' };
      }

      if (contactNumber.startsWith('+')) {
        return { type: 'client-error', message: 'Contact number cannot start with +' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.otp({
        uuid,
        appId,
        contactNumber: contactNumber.replace(/-/g, ''),
        channel: 'register',
      });
      set({ isLoading: false });

      if (response.type === 'success') {
        set((state) => ({
          form: { ...state.form, refCode: response.model.refcode ?? '' },
          otpExpireInSeconds: response.model.expireinseconds ?? 180,
          error: null,
        }));
      } else {
        set({ error: response });
      }

      return response;
    },

    validateOtp: async () => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      const appId = useBuzzebeesAppStore.getState().appId;
      const form = get().form;
      const otp = form.otp.trim();
      const refCode = form.refCode.trim();
      const contactNumber = form.contactNumber.trim();

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      if (otp === '') {
        return { type: 'client-error', message: 'OTP is required' };
      }

      if (refCode === '') {
        return { type: 'client-error', message: 'OTP Ref code is required' };
      }

      if (contactNumber === '') {
        return { type: 'client-error', message: 'Contact number is required' };
      }

      if (contactNumber.startsWith('+')) {
        return { type: 'client-error', message: 'Contact number cannot start with +' };
      }

      const type = contactNumber.includes('@') ? 'email' : 'contact_number';

      set({ isLoading: true });
      const response = await bzbsService.authApi.validateOtp({
        appId,
        otp,
        refCode,
        type,
        contactNumber: contactNumber.replace(/-/g, ''),
        use: false,
        channel: 'register',
      });
      set({ isLoading: false });

      if (response.type === 'success') {
        set({ error: null });
      } else {
        set({ error: response });
      }

      return response;
    },

    validateRegister: async () => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      const appId = useBuzzebeesAppStore.getState().appId;
      const form = get().form;
      const username = form.username.trim();
      const email = form.email?.trim();
      const contactNumber = form.contactNumber.trim();

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      if (username === '') {
        return { type: 'client-error', message: 'Username is required' };
      }

      if (email === '') {
        return { type: 'client-error', message: 'Email is required' };
      }

      if (!email || !isValidEmail(email)) {
        return { type: 'client-error', message: 'Email is invalid' };
      }

      if (contactNumber === '') {
        return { type: 'client-error', message: 'Contact number is required' };
      }

      if (contactNumber.startsWith('+')) {
        return { type: 'client-error', message: 'Contact number cannot start with +' };
      }

      set({ isLoading: true });
      const response = await bzbsService.registerApi.validateRegister({
        appId,
        username,
        email,
        contactNumber: contactNumber.replace(/-/g, ''),
      });
      set({ isLoading: false });

      if (response.type === 'success') {
        set((state) => ({
          form: { ...state.form, refCode: response.model.refcode ?? '' },
          otpExpireInSeconds: response.model.expireinseconds ?? 180,
          error: null,
        }));
      } else {
        set({ error: response });
      }

      return response;
    },

    register: async () => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      const uuid = useBuzzebeesAppStore.getState().uuid;
      const appId = useBuzzebeesAppStore.getState().appId;
      const macAddress = useBuzzebeesAppStore.getState().macAddress;
      const os = useBuzzebeesAppStore.getState().os;
      const platform = useBuzzebeesAppStore.getState().platform;
      const clientVersion = useBuzzebeesAppStore.getState().clientVersion;
      const deviceNotificationEnable = useBuzzebeesAppStore.getState().deviceNotificationEnabled;
      const form = get().form;
      const consentVersion = get().consentVersion;

      const deviceLocale = useLocaleStore.getState().localeId;

      // Trim all string fields
      const username = form.username.trim();
      const password = form.password.trim();
      const confirmPassword = form.confirmPassword.trim();
      const firstName = form.firstName.trim();
      const lastName = form.lastName.trim();
      const contactNumber = form.contactNumber.trim().replace(/-/g, '');
      const otp = form.otp.trim();
      const refCode = form.refCode.trim();
      const email = form.email?.trim();
      const zipCode = form.zipCode?.trim();
      const info = form.info?.trim();
      const address = form.address?.trim();
      const gender = form.gender?.trim();
      const birthdate = form.birthDate;
      const refUserCode = form.refUserCode?.trim();
      const emailMarketing = form.emailMarketingAccepted ? '1' : '0';
      const smsMarketing = form.smsMarketingAccepted ? '1' : '0';
      const notificationMarketing = form.notificationMarketingAccepted ? '1' : '0';
      const lineMarketing = form.lineMarketingAccepted ? '1' : '0';
      const phoneMarketing = form.phoneMarketingAccepted ? '1' : '0';

      // If consent age is not provided, use the consent age accepted value
      const resolvedConsentAge = form.consentAge ?? (form.consentAgeAccepted ? 1 : 0);

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      if (!consentVersion) {
        return { type: 'client-error', message: 'Consent version not found' };
      }

      const termAndConditionVersion = consentVersion.termAndCondition;
      const dataPrivacyVersion = consentVersion.dataPrivacy;
      const marketingOptionsVersion = consentVersion.marketingOptions;

      if (!termAndConditionVersion || !dataPrivacyVersion || !marketingOptionsVersion) {
        return { type: 'client-error', message: 'Consent version is invalid' };
      }

      if (!/^\d+$/.test(termAndConditionVersion)) {
        return { type: 'client-error', message: 'Term and condition version must be a number string' };
      }

      if (!/^\d+$/.test(dataPrivacyVersion)) {
        return { type: 'client-error', message: 'Data privacy version must be a number string' };
      }

      if (!/^\d+$/.test(marketingOptionsVersion)) {
        return { type: 'client-error', message: 'Marketing options version must be a number string' };
      }

      if (username === '') {
        return { type: 'client-error', message: 'Username is required' };
      }

      if (password === '') {
        return { type: 'client-error', message: 'Password is required' };
      }

      if (confirmPassword === '') {
        return { type: 'client-error', message: 'Confirm password is required' };
      }

      if (password !== confirmPassword) {
        return { type: 'client-error', message: 'Password and confirm password do not match' };
      }

      if (firstName === '') {
        return { type: 'client-error', message: 'First name is required' };
      }

      if (lastName === '') {
        return { type: 'client-error', message: 'Last name is required' };
      }

      if (contactNumber === '') {
        return { type: 'client-error', message: 'Contact number is required' };
      }

      if (contactNumber.startsWith('+')) {
        return { type: 'client-error', message: 'Contact number cannot start with +' };
      }

      if (otp === '') {
        return { type: 'client-error', message: 'OTP is required' };
      }

      if (refCode === '') {
        return { type: 'client-error', message: 'OTP Ref code is required' };
      }

      if (email && !isValidEmail(email)) {
        return { type: 'client-error', message: 'Email is invalid' };
      }

      set({ isLoading: true });
      const response = await bzbsService.registerApi.register({
        appId,
        uuid,
        macAddress,
        os,
        platform,
        clientVersion,
        deviceNotificationEnable,
        username,
        password,
        confirmPassword,
        firstName,
        lastName,
        contactNumber,
        otp,
        refCode,
        address,
        gender,
        birthdate,
        email,
        refUserCode,
        info,
        termAndConditionVersion,
        dataPrivacyVersion,
        marketingOptionsVersion,
        consentAge: resolvedConsentAge.toString(),
        emailMarketing,
        smsMarketing,
        notificationMarketing,
        lineMarketing,
        phoneMarketing,
        options: { device_locale: deviceLocale, zip_code: zipCode, ...form.additionalInfo },
      });
      set({ isLoading: false });

      if (response.type === 'success') {
        set({ error: null });
        trackCommonEvents.registerSuccess();
      } else {
        set({ error: response });
      }

      return response;
    },

    setConsentVersion: (termAndCondition: string, dataPrivacy: string, marketingOptions: string) => {
      if (!/^\d+$/.test(termAndCondition)) {
        console.error('Term and condition version must be a number string, received:', termAndCondition);
        return;
      }

      if (!/^\d+$/.test(dataPrivacy)) {
        console.error('Data privacy version must be a number string, received:', dataPrivacy);
        return;
      }

      if (!/^\d+$/.test(marketingOptions)) {
        console.error('Marketing options version must be a number string, received:', marketingOptions);
        return;
      }

      set({
        consentVersion: {
          termAndCondition,
          dataPrivacy,
          marketingOptions,
        },
      });
    },

    clear: () => {
      set({
        form: { ...initialFormData },
        consentVersion: initialConsentVersion,
        isLoading: false,
        error: null,
      });
    },
  };
});
